import { Action, createReducer, on } from '@ngrx/store'
import {
  DEFAULT_INCEPTION_DATE,
  DEFAULT_TRACKING_SELECTORS,
  TrackingData,
  TrackingPreferences,
  TrackingSelectors,
  TrackingViewsData,
  Comment,
} from '../tracking.model'
import * as fromTrackingActions from './tracking.actions'
import { IControl } from 'src/app/management-information/store/management-information.reducer'
import { getSelectors, getSelectorsForView } from '../tracking.utils'
import { BasicControl } from 'src/app/management-information/model/management-information.model'
import { SelectedUserPreferences } from 'src/app/core/model/reinsurer.model'

export const TRACKING_KEY = 'tracking'

export interface TrackingState {
  trackingData: TrackingData[]
  trackingMyViewsData: TrackingViewsData[] | null
  trackingTeamViewsData: TrackingViewsData[] | null
  trackingClientViewsData: TrackingViewsData[] | null
  selectors: TrackingSelectors
  myViewSelectors: TrackingSelectors
  teamViewSelectors: TrackingSelectors
  inceptionTimeInterval: BasicControl
  trackingUserPreferences: SelectedUserPreferences[]
  selectedUserPreference: number | null | undefined
  isLoading: boolean
  isPreferenceLoading: boolean
  isDetailsLoading: boolean
}

const initialState: TrackingState = {
  trackingData: [],
  trackingMyViewsData: null,
  trackingTeamViewsData: null,
  trackingClientViewsData: null,
  selectors: DEFAULT_TRACKING_SELECTORS,
  myViewSelectors: DEFAULT_TRACKING_SELECTORS,
  teamViewSelectors: DEFAULT_TRACKING_SELECTORS,
  inceptionTimeInterval: DEFAULT_INCEPTION_DATE,
  trackingUserPreferences: [],
  selectedUserPreference: undefined,
  isLoading: false,
  isPreferenceLoading: false,
  isDetailsLoading: false,
}

const trackingReducer = createReducer(
  initialState,
  on(fromTrackingActions.fetchTracking, (state): TrackingState => {
    return {
      ...state,
      isLoading: true,
      isPreferenceLoading: true,
      selectedUserPreference: undefined,
    }
  }),
  on(
    fromTrackingActions.fetchTrackingSuccess,
    (state: TrackingState, { trackingData, selectedYear }) => {
      const updatedSelectors = { ...state.selectors }
      const year = { ...state.selectors.year }
      year.selectedValues = [selectedYear]
      updatedSelectors.year = year
      return {
        ...state,
        trackingData,
        selectors: getSelectors(updatedSelectors, trackingData),
        isLoading: false,
      }
    },
  ),
  on(fromTrackingActions.fetchTrackingFailure, (state) => {
    return {
      ...state,
      isLoading: false,
      isPreferenceLoading: false,
    }
  }),

  on(fromTrackingActions.filtersChanged, (state: TrackingState, { filters }) => {
    if (!filters) {
      return state
    }
    const newSelectors: TrackingSelectors = { ...state.selectors }
    const entries: [string, IControl][] = Object.entries(state.selectors)
    for (const [selectorKey, selector] of entries) {
      newSelectors[selectorKey as keyof TrackingSelectors] = {
        ...selector,
        selectedValues: filters[selectorKey] ?? [],
      }
    }
    return { ...state, selectors: newSelectors, selectedUserPreference: null }
  }),

  on(fromTrackingActions.filtersChangedMyView, (state: TrackingState, { filters }) => {
    if (!filters) {
      return state
    }
    const newSelectors: TrackingSelectors = { ...state.myViewSelectors }
    const entries: [string, IControl][] = Object.entries(state.myViewSelectors)
    for (const [selectorKey, selector] of entries) {
      newSelectors[selectorKey as keyof TrackingSelectors] = {
        ...selector,
        selectedValues: filters[selectorKey] ?? [],
      }
    }
    return { ...state, myViewSelectors: newSelectors, selectedUserPreference: null }
  }),

  on(fromTrackingActions.filtersChangedTeamView, (state: TrackingState, { filters }) => {
    if (!filters) {
      return state
    }
    const newSelectors: TrackingSelectors = { ...state.teamViewSelectors }
    const entries: [string, IControl][] = Object.entries(state.teamViewSelectors)
    for (const [selectorKey, selector] of entries) {
      newSelectors[selectorKey as keyof TrackingSelectors] = {
        ...selector,
        selectedValues: filters[selectorKey] ?? [],
      }
    }
    return { ...state, teamViewSelectors: newSelectors, selectedUserPreference: null }
  }),

  on(
    fromTrackingActions.inceptionDateChanged,
    (state: TrackingState, { minInceptionDate, maxInceptionDate }) => {
      const updatedState: TrackingState = {
        ...state,
        inceptionTimeInterval: {
          ...state.inceptionTimeInterval,
          minValue: minInceptionDate,
          maxValue: maxInceptionDate,
        },
        selectedUserPreference: null,
      }
      return updatedState
    },
  ),
  on(fromTrackingActions.fetchTrackingDetails, (state) => {
    return {
      ...state,
      isDetailsLoading: true,
    }
  }),
  on(
    fromTrackingActions.fetchTrackingDetailsSuccess,
    (state: TrackingState, { trackingDetailsData }) => {
      return {
        ...state,
        trackingData: state.trackingData.map((existingData) =>
          trackingDetailsData &&
          trackingDetailsData[0] &&
          existingData.programId === trackingDetailsData[0].programId
            ? { ...existingData, details: trackingDetailsData }
            : existingData,
        ),
        isDetailsLoading: false,
      }
    },
  ),
  on(fromTrackingActions.fetchTrackingDetailsFailure, (state) => {
    return {
      ...state,
      isDetailsLoading: false,
    }
  }),

  on(
    fromTrackingActions.updateCompleteProgramSuccess,
    (state: TrackingState, { programEvents }) => {
      return {
        ...state,
        trackingData: state.trackingData.map((existingData) =>
          programEvents && existingData.programId.toString() === programEvents.programId.toString()
            ? { ...existingData, complete: 'Yes' }
            : existingData,
        ),
      }
    },
  ),

  on(
    fromTrackingActions.saveTrackingPreferencesSuccess,
    (state: TrackingState, { userPreferences }) => {
      const updatedPreference = state.trackingUserPreferences
      return {
        ...state,
        trackingUserPreferences: [...updatedPreference, userPreferences],
      }
    },
  ),

  on(
    fromTrackingActions.getTrackingPreferencesSuccess,
    (state, { userPreferences, selectedPreferenceId }) => {
      let newState = {
        ...state,
        trackingUserPreferences: userPreferences,
        isPreferenceLoading: false,
      }

      if (selectedPreferenceId && state.selectedUserPreference === undefined) {
        // Auto-apply the preference
        const preferenceFound = userPreferences.find((p) => p.id === selectedPreferenceId)
        if (preferenceFound) {
          const updatedSelectors = JSON.parse(JSON.stringify(state.teamViewSelectors))
          const updatedTime = JSON.parse(JSON.stringify(state.inceptionTimeInterval))
          const selectedValues = JSON.parse(preferenceFound.preferenceValue)

          updatedSelectors.clientName.selectedValues = selectedValues.clientName
          updatedSelectors.businessUnit.selectedValues = selectedValues.businessUnit
          updatedSelectors.year.selectedValues = selectedValues.year
          updatedSelectors.complete.selectedValues = selectedValues.complete
          updatedSelectors.reinsurerNames.selectedValues = selectedValues.reinsurerNames

          updatedTime.minValue = selectedValues.minDateValue
          updatedTime.maxValue = selectedValues.maxDateValue

          newState = {
            ...newState,
            selectedUserPreference: selectedPreferenceId,
            teamViewSelectors: updatedSelectors,
            inceptionTimeInterval: updatedTime,
            isPreferenceLoading: false,
          }
        }
      }

      return newState
    },
  ),

  on(fromTrackingActions.getTrackingPreferencesFailure, (state) => {
    return {
      ...state,
      isLoading: false,
      isPreferenceLoading: false,
    }
  }),

  on(fromTrackingActions.setSelectedUserPreferences, (state: TrackingState, { id }) => {
    const preferenceFound = state.trackingUserPreferences.find((t) => t.id === id)
    if (preferenceFound) {
      const updatedSelectors = JSON.parse(JSON.stringify(state.selectors))
      const updatedTime = JSON.parse(JSON.stringify(state.inceptionTimeInterval))
      const selectedPreferenceValues = JSON.parse(
        preferenceFound.preferenceValue,
      ) as TrackingPreferences
      if (selectedPreferenceValues) {
        updatedSelectors.clientName.selectedValues = selectedPreferenceValues.clientName
        updatedSelectors.businessUnit.selectedValues = selectedPreferenceValues.businessUnit
        updatedSelectors.year.selectedValues = selectedPreferenceValues.year
        updatedSelectors.complete.selectedValues = selectedPreferenceValues.complete
        updatedSelectors.reinsurerNames.selectedValues = selectedPreferenceValues.reinsurerNames
        updatedTime.minValue = selectedPreferenceValues.minDateValue
        updatedTime.maxValue = selectedPreferenceValues.maxDateValue
      }

      return {
        ...state,
        selectedUserPreference: id,
        selectors: updatedSelectors,
        inceptionTimeInterval: updatedTime,
      }
    } else {
      return {
        ...state,
      }
    }
  }),

  on(fromTrackingActions.updateUserPreferenceSuccess, (state, { updatedPreferences }) => {
    const updatedPrefs = state.trackingUserPreferences.map((p) =>
      p.id === updatedPreferences.id ? updatedPreferences : p,
    )

    const isSelected = state.selectedUserPreference === updatedPreferences.id

    return {
      ...state,
      trackingUserPreferences: updatedPrefs,
      selectedUserPreference: isSelected
        ? updatedPreferences.id
        : (state.selectedUserPreference ?? null),
    }
  }),

  on(fromTrackingActions.deleteUserPreferenceSuccess, (state, { deletedPreferences }) => {
    const updatedPreferences = state.trackingUserPreferences.filter(
      (p) => p.id !== deletedPreferences.id,
    )
    const isDeletedSelected = state.selectedUserPreference === deletedPreferences.id

    return {
      ...state,
      trackingUserPreferences: updatedPreferences,
      selectedUserPreference: isDeletedSelected ? null : state.selectedUserPreference,
    }
  }),

  on(fromTrackingActions.fetchMyViewTracking, (state) => {
    return {
      ...state,
      isLoading: true,
    }
  }),

  on(
    fromTrackingActions.fetchMyViewTrackingSuccess,
    (state: TrackingState, { trackingMyViewsData, selectedYear }) => {
      const baseSelectors = selectedYear ? state.selectors : state.myViewSelectors
      const updatedSelectors = { ...baseSelectors, year: { ...baseSelectors.year } }
      if (selectedYear) {
        updatedSelectors.year.selectedValues = [selectedYear]
      }
      return {
        ...state,
        trackingMyViewsData,
        myViewSelectors: getSelectorsForView(updatedSelectors, trackingMyViewsData),
        isLoading: false,
      }
    },
  ),

  on(fromTrackingActions.fetchMyViewTrackingFailure, (state) => {
    return {
      ...state,
      isLoading: false,
    }
  }),

  on(fromTrackingActions.fetchTeamViewTracking, (state) => {
    return {
      ...state,
      isLoading: true,
    }
  }),

  on(
    fromTrackingActions.fetchTeamViewTrackingSuccess,
    (state: TrackingState, { trackingTeamViewsData, selectedYear }) => {
      const baseSelectors = selectedYear ? state.selectors : state.teamViewSelectors
      const updatedSelectors = { ...baseSelectors, year: { ...baseSelectors.year } }
      if (selectedYear) {
        updatedSelectors.year.selectedValues = [selectedYear]
      }
      return {
        ...state,
        trackingTeamViewsData,
        teamViewSelectors: getSelectorsForView(updatedSelectors, trackingTeamViewsData),
        isLoading: false,
      }
    },
  ),

  on(fromTrackingActions.fetchTeamViewTrackingFailure, (state) => {
    return {
      ...state,
      isLoading: false,
    }
  }),

  on(fromTrackingActions.fetchClientViewTracking, (state) => {
    return {
      ...state,
      isLoading: true,
    }
  }),

  on(
    fromTrackingActions.fetchClientViewTrackingSuccess,
    (state: TrackingState, { trackingClientViewsData }) => {
      return {
        ...state,
        trackingClientViewsData: trackingClientViewsData,
        isLoading: false,
      }
    },
  ),

  on(fromTrackingActions.fetchClientViewTrackingFailure, (state) => {
    return {
      ...state,
      isLoading: false,
    }
  }),

  on(
    fromTrackingActions.updateUserValueSuccess,
    (state: TrackingState, { opportunityId, role, email_ids }) => {
      const roleKey = (role ?? '').toLowerCase().replace(/\s+/g, '')
      const rolePropMap: Record<string, keyof TrackingViewsData> = {
        accountexecutive: 'accountExecutive',
        support: 'support',
        analytics: 'analytics',
      }
      const roleProp = rolePropMap[roleKey]
      if (!roleProp) return state

      const updatedUsers = (email_ids ?? []).map((email) => ({
        email_id: email,
        name: (email || '').split('@')[0],
      }))

      const applyUpdate = (dataset: TrackingViewsData[] | null | undefined) =>
        (dataset ?? []).map((item) =>
          item.opportunityId === opportunityId ? { ...item, [roleProp]: updatedUsers } : item,
        )

      return {
        ...state,
        trackingMyViewsData: applyUpdate(state.trackingMyViewsData),
        trackingTeamViewsData: applyUpdate(state.trackingTeamViewsData),
      }
    },
  ),

  on(
    fromTrackingActions.updateColumnValueSuccess,
    (state: TrackingState, { opportunityId, category, newValue, dateTime }) => {
      if (!category) return state

      const safeCategory = category.toLowerCase()

      const applyUpdate = (list: any[] | undefined) => {
        const source = Array.isArray(list) ? list : []

        return source.map((item) => {
          if (item.opportunityId !== opportunityId) return item

          const safeColumns = Array.isArray(item.columns) ? item.columns : []
          const safeAuditColumns = Array.isArray(item.auditColumns) ? item.auditColumns : []

          // Update existing columns
          const updatedColumns = safeColumns.map((col: any) => {
            if (!col?.columnName) return col

            if (col.columnName.toLowerCase() !== safeCategory) return col

            // Update valueHistory if we have a date
            const nextValueHistory = dateTime
              ? upsertValueHistory(col.valueHistory, newValue, dateTime)
              : col.valueHistory

            return {
              ...col,
              value: newValue,
              updatedDate: dateTime,
              updatedSource: 'Manual',
              valueHistory: nextValueHistory,
            }
          })

          // Column check
          const columnExists = safeColumns.some(
            (col: any) => col?.columnName?.toLowerCase() === safeCategory,
          )

          // If column doesn't exist → create a new one
          const finalColumns = columnExists
            ? updatedColumns
            : [
                ...safeColumns,
                {
                  columnName: category,
                  value: newValue,
                  updatedDate: dateTime,
                  updatedSource: 'Manual',
                  comments: [],
                  valueHistory: dateTime ? upsertValueHistory([], newValue, dateTime) : [],
                },
              ]

          // Always append to auditColumns if there is a date
          let finalAuditColumns = safeAuditColumns

          if (dateTime) {
            const newAuditEntry = {
              columnName: category,
              value: newValue,
              updatedDate: dateTime,
              updatedSource: 'Manual',
              auditCreatedDate: new Date().toISOString(),
            }

            finalAuditColumns = [...safeAuditColumns, newAuditEntry]
          }

          return {
            ...item,
            columns: finalColumns,
            auditColumns: finalAuditColumns,
          }
        })
      }

      return {
        ...state,
        trackingTeamViewsData: applyUpdate(state.trackingTeamViewsData!),
        trackingMyViewsData: applyUpdate(state.trackingMyViewsData!),
      }
    },
  ),
  on(
    fromTrackingActions.updateCommentSuccess,
    (state: TrackingState, { opportunityId, category, comment }) => {
      if (!category) return state

      const safeCategory = category.toLowerCase()

      const applyUpdate = (list: any[] | undefined) => {
        const source = Array.isArray(list) ? list : []
        return source.map((item) => {
          if (item.opportunityId !== opportunityId) return item

          const safeColumns = Array.isArray(item.columns) ? item.columns : []

          const updatedColumns = safeColumns.map((col: { columnName: string }) => {
            if (!col?.columnName) return col
            if (col.columnName.toLowerCase() === safeCategory) {
              return {
                ...col,
                comments: [
                  {
                    author: '',
                    authorEmail: '',
                    text: comment,
                    createdDate: new Date().toISOString(),
                  },
                ],
                updatedDate: new Date().toISOString(),
                updatedSource: 'Manual',
              }
            }
            return col
          })

          const columnExists = safeColumns.some(
            (col: { columnName: string }) =>
              col?.columnName && col.columnName.toLowerCase() === safeCategory,
          )

          const finalColumns = columnExists
            ? updatedColumns
            : [
                ...safeColumns,
                {
                  columnName: category,
                  value: '',
                  updatedDate: new Date().toISOString(),
                  updatedSource: 'Manual',
                  comments: [
                    {
                      author: '',
                      authorEmail: '',
                      text: comment,
                      createdDate: new Date().toISOString(),
                    },
                  ],
                },
              ]

          return { ...item, columns: finalColumns }
        })
      }

      return {
        ...state,
        trackingTeamViewsData: applyUpdate(state.trackingTeamViewsData!),
        trackingMyViewsData: applyUpdate(state.trackingMyViewsData!),
      }
    },
  ),
)

function upsertValueHistory(
  existing: Array<{ value: string; updatedDate: string }> | undefined,
  value: any,
  updatedDate?: string | null,
) {
  const list = Array.isArray(existing) ? [...existing] : []
  const key = (value ?? '').toString().trim().toLowerCase()

  if (!key || !updatedDate) return list

  const idx = list.findIndex((x) => (x.value ?? '').toString().trim().toLowerCase() === key)

  const entry = { value, updatedDate }

  if (idx >= 0) {
    // update existing history record
    list[idx] = { ...list[idx], ...entry }
  } else {
    // insert new history record
    list.push(entry)
  }

  return list
}

export function reducer(state: TrackingState | undefined, action: Action): TrackingState {
  return trackingReducer(state, action)
}
